import React from "react";
import { CLUB_STATUS_ORDER, clubStatusMeta } from "../../../constants/clubRadarChoices";
import {
  Eyebrow,
  PrimaryButton,
  SecondaryButton,
  TextButton,
  BottomSheetShell,
  SHADOW,
  statusStyle,
} from "../v2";
// The list rows + "+ New list" row live one level up (ClubRadarListPickRows) since
// the scout-request close dialog picks a target list with the SAME affordance.
import { ListRow, InlineNewList } from "../ClubRadarListPickRows";

// Club "Add to Radar" picker — Club-Radar UI Rebuild V2, frames 1l (desktop
// popover, 372px, anchored to the profile-header CTA) and 1m (mobile bottom
// sheet). Presentation only: parent (ClubAddToRadarButton) owns lists, the
// checked draft, optional status + note, and the save-from-profile submit —
// the prop contract is unchanged from the pre-rebuild picker.
//
// States per DESIGN-SPEC `pickLists`: already-in (green pre-checked look,
// NOT toggleable, "Already in this list" caption) · selected (blue checkbox)
// · unselected. "+ New list" keeps the existing inline-create behavior
// (dashed box row → input + Add), styled to the frame.

// Selected status chip: tint bg + strong fg (kit STATUS_STYLES) + 1.5px
// border in the status's strong hue. The artboard shows Watching selected
// with border #2283FF (1l) and Recommended selected with border #16A34A
// (1j segment pattern) — the remaining hues follow the same saturated-600
// logic. Data-driven over the platform's FOUR statuses (mock chips are
// sample data, never a limit — Brief Rule 10).
const STATUS_SELECTED_BORDER = {
  watching: "#2283FF",
  recommended: "#16A34A",
  rejected: "#DC2626",
  shortlisted: "#D97706",
};

const StatusChips = ({ status, setStatus, mobile }) => (
  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
    {CLUB_STATUS_ORDER.map((s) => {
      const meta = clubStatusMeta(s);
      const active = status === s;
      const st = statusStyle(s);
      return (
        <button
          key={s}
          type="button"
          onClick={() => setStatus(active ? null : s)}
          aria-pressed={active}
          style={{
            display: "inline-flex",
            alignItems: "center",
            boxSizing: "border-box",
            padding: mobile ? "6px 12px" : "5px 11px",
            borderRadius: 999,
            fontSize: mobile ? 12.5 : 12,
            fontWeight: active ? 700 : 600,
            fontFamily: "inherit",
            cursor: "pointer",
            whiteSpace: "nowrap",
            border: active
              ? `1.5px solid ${STATUS_SELECTED_BORDER[s] || "#2283FF"}`
              : "1px solid #E5E7EB",
            background: active ? st.bg : "#fff",
            color: active ? st.fg : "#6B7280",
          }}
        >
          {meta.label}
        </button>
      );
    })}
  </div>
);

const ClubSaveToRadarPicker = ({
  isMobile,
  lists = [],
  draftListIds,
  onToggle,
  onCreateList,
  creating,
  status,
  setStatus,
  note,
  setNote,
  onSubmit,
  onCancel,
  playerName,
  clubName,
}) => {
  const stop = (e) => e.stopPropagation();
  const addCount = draftListIds.length;
  const ctaLabel = addCount <= 1 ? "Add to 1 list" : `Add to ${addCount} lists`;

  const header = (
    <>
      <div style={{ fontSize: isMobile ? 15 : 14.5, fontWeight: 800, color: "#111827" }}>Add to Radar</div>
      {playerName && (
        <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 2 }}>{playerName}</div>
      )}
    </>
  );

  // APP-1 fix: only the LIST rows scroll; "+ New list" is pinned below the
  // scroll region so it can never sit half-clipped at the fold (the artboard
  // shows it as the always-visible last row — mock list counts are sample
  // data, never a limit, Brief Rule 10). A partially visible list row at the
  // fold is the scroll affordance.
  const listBlock = (
    <div style={{ marginTop: isMobile ? 8 : 10 }}>
      <div
        data-testid="cr-picker-list-scroll"
        style={{ maxHeight: isMobile ? 250 : 210, overflowY: "auto", overscrollBehavior: "contain" }}
      >
        {lists.map((l) => (
          <ListRow key={l.id} list={l} checked={draftListIds.includes(l.id)} onToggle={onToggle} mobile={isMobile} />
        ))}
      </div>
      <InlineNewList mobile={isMobile} creating={creating} onCreateList={onCreateList} />
    </div>
  );

  if (isMobile) {
    // ── Frame 1m — bottom sheet (kit shell: scrim .44, r 20 20 0 0,
    // grab handle, pad 10px 16px 18px) ─────────────────────────────────
    return (
      <BottomSheetShell open onClose={onCancel}>
        {header}
        {listBlock}
        <Eyebrow spacing=".07em" style={{ marginTop: 10 }}>Status · optional</Eyebrow>
        <StatusChips status={status} setStatus={setStatus} mobile />
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={`Note (optional) — visible to ${clubName || "club"} staff`}
          className="w-full outline-none resize-none"
          style={{
            marginTop: 12,
            height: 52,
            boxSizing: "border-box",
            border: "1px solid #E5E7EB",
            borderRadius: 11,
            padding: "9px 12px",
            fontSize: 12.5,
            lineHeight: 1.4,
            fontFamily: "inherit",
            display: "block",
          }}
        />
        <PrimaryButton
          height={46}
          radius={12}
          fontSize={14}
          disabled={addCount === 0}
          onClick={onSubmit}
          style={{ marginTop: 14, width: "100%" }}
        >
          {ctaLabel}
        </PrimaryButton>
        <TextButton height={40} fontSize={13} onClick={onCancel} style={{ marginTop: 8, width: "100%" }}>
          Cancel
        </TextButton>
      </BottomSheetShell>
    );
  }

  // ── Frame 1l — desktop popover (w372, r16, picker shadow, pad 16),
  // anchored below the profile-header CTA in the real shell ────────────
  return (
    <div
      onClick={stop}
      role="dialog"
      aria-label="Add to Radar"
      style={{
        position: "absolute",
        right: 0,
        top: 46,
        zIndex: 30,
        width: 372,
        maxWidth: "calc(100vw - 32px)",
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 16,
        boxShadow: SHADOW.picker,
        padding: 16,
        animation: "tp-pop 0.12s ease",
      }}
    >
      {header}
      {listBlock}
      <div style={{ height: 1, background: "#F1F5F9", margin: "8px 0 12px" }} />
      <Eyebrow spacing=".07em">Status · optional</Eyebrow>
      <StatusChips status={status} setStatus={setStatus} />
      <Eyebrow spacing=".07em" style={{ marginTop: 14 }}>Note · optional</Eyebrow>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={`Why is he on your radar? Visible to ${clubName || "club"} staff on shared lists.`}
        className="w-full outline-none resize-none"
        style={{
          marginTop: 8,
          height: 58,
          boxSizing: "border-box",
          border: "1px solid #E5E7EB",
          borderRadius: 10,
          padding: "9px 12px",
          fontSize: 12.5,
          lineHeight: 1.4,
          fontFamily: "inherit",
          display: "block",
        }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <SecondaryButton height={36} radius={10} fontSize={13} pad="0 14px" onClick={onCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton height={36} radius={10} fontSize={13} pad="0 14px" disabled={addCount === 0} onClick={onSubmit}>
          {ctaLabel}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default ClubSaveToRadarPicker;
